import React, { useState, useRef, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useLocation, useNavigate } from 'react-router-dom';
import EquipmentPlanner from './EquipmentPlanner';
import ConsolidatedTracker from './ConsolidatedTracker';
import EquipmentPlanCreate from './EquipmentPlanCreate';
import AreaPlanCreate from './AreaPlanCreate';
import AreaConsolidatedTracker from './AreaConsolidatedTracker';
import AreaPlanVsActual from './AreaPlanVsActual';
import AreaLogNew from './AreaLogNew';
import AreaMicroMonitoring from './AreaMicroMonitoring';
import AreaLog from './AreaLog';
import PhysicalLogEntry from './PhysicalLogEntry';
import VolumeSummary from './VolumeSummary';
import EquipmentLogAccuracyDashboard from './EquipmentLogAccuracyDashboard';
import PlanCycleVariance from './PlanCycleVariance';
import CleaningTimeVariance from './CleaningTimeVariance';
import ATReport from './ATReport';
import ProductionVarianceDashboard from './ProductionVarianceDashboard';
import PhysicalConsolidatedTracker from './PhysicalConsolidatedTracker';
import PhysicalVolumeSummary from './PhysicalVolumeSummary';
import PhysicalProductionVarianceDashboard from './PhysicalProductionVarianceDashboard';
import BatchTracker from './BatchTracker';
import OsdDispatch from './OsdDispatch';
import PackingDispatch from './PackingDispatch';
import ManufacturingDispatch from './ManufacturingDispatch';
import RndSampleTracking from './rnd/index';
import RndEquipmentPlanCreate from './RndEquipmentPlanCreate';
import RndPhysicalLogEntry from './RndPhysicalLogEntry';

const Navigation = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [statsDropdownOpen, setStatsDropdownOpen] = useState(false);
  const [physicalDropdownOpen, setPhysicalDropdownOpen] = useState(false);
  const [rndDropdownOpen, setRndDropdownOpen] = useState(false);
  const [areaDropdownOpen, setAreaDropdownOpen] = useState(false);
  const dropdownRef = useRef(null);
  const physicalDropdownRef = useRef(null);
  const rndDropdownRef = useRef(null);
  const areaDropdownRef = useRef(null);

  // Physical Log Insights submenu items
  const physicalMenuItems = [
    { path: '/physical-log-insights/tracker', label: '📋 Consolidated Tracker (A)' },
    { path: '/physical-log-insights/volume-summary', label: '📊 Volume Summary (A)' },
    { path: '/physical-log-insights/production-variance', label: '📈 Production Variance (A)' },
  ];

  const isPhysicalActive = physicalMenuItems.some(item => location.pathname === item.path);

  // R&D Analytics submenu items
  const rndMenuItems = [
    { path: '/rnd', label: '🧪 R&D Sample Tracking' },
    { path: '/rnd/plan-create', label: '📝 R&D Create Plan' },
    { path: '/rnd/physical-log', label: '📋 R&D Physical Log' },
  ];

  const isRndActive = rndMenuItems.some(item => location.pathname === item.path);

  // Area Planning submenu items
  const areaMenuItems = [
    { path: '/area-plan-create', label: '📝 Create Plan (Area)' },
    { path: '/area-tracker', label: '📊 Area Consolidated Tracker' },
    { path: '/area-plan-vs-actual', label: '⚖️ Plan vs Actual (Area)' },
    { path: '/area-log-new', label: '📋 Area Log (New)' },
    { path: '/area-micro-monitoring', label: '🔬 Micro Level Monitoring' },
  ];

  const isAreaActive = areaMenuItems.some(item => location.pathname === item.path);

  // Equipment Log Insights submenu items
  const statsMenuItems = [
    { path: '/tracker', label: '📋 Consolidated Tracker' },
    { path: '/volume-summary', label: '📊 Volume Summary' },
    { path: '/log-accuracy', label: '📈 Log Accuracy' },
    { path: '/production-variance', label: '📋 Production Variance' },
    { path: '/plan-cycle-variance', label: '📉 Plan Variance' },
    { path: '/cleaning-variance', label: '🧹 Cleaning Variance' },
  ];

  const isStatsActive = statsMenuItems.some(item => location.pathname === item.path);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setStatsDropdownOpen(false);
      }
      if (physicalDropdownRef.current && !physicalDropdownRef.current.contains(event.target)) {
        setPhysicalDropdownOpen(false);
      }
      if (rndDropdownRef.current && !rndDropdownRef.current.contains(event.target)) {
        setRndDropdownOpen(false);
      }
      if (areaDropdownRef.current && !areaDropdownRef.current.contains(event.target)) {
        setAreaDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  
  return (
    <nav style={{
      backgroundColor: 'white',
      padding: '12px 20px',
      boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
      display: 'flex',
      gap: '20px',
      alignItems: 'center'
    }}>
      <Link 
        to="/" 
        style={{
          color: location.pathname === '/' ? '#3b82f6' : '#6b7280',
          textDecoration: 'none',
          fontWeight: location.pathname === '/' ? '600' : '500',
          fontSize: '14px',
          padding: '8px 16px',
          borderRadius: '6px',
          backgroundColor: location.pathname === '/' ? '#eff6ff' : 'transparent',
          transition: 'all 0.2s'
        }}
      >
        Equipment Log
      </Link>
      <Link 
        to="/plan-create" 
        style={{
          color: location.pathname === '/plan-create' ? '#3b82f6' : '#6b7280',
          textDecoration: 'none',
          fontWeight: location.pathname === '/plan-create' ? '600' : '500',
          fontSize: '14px',
          padding: '8px 16px',
          borderRadius: '6px',
          backgroundColor: location.pathname === '/plan-create' ? '#eff6ff' : 'transparent',
          transition: 'all 0.2s'
        }}
      >
        Create Plan
      </Link>
      <Link
        to="/area-log"
        style={{
          color: location.pathname === '/area-log' ? '#3b82f6' : '#6b7280',
          textDecoration: 'none',
          fontWeight: location.pathname === '/area-log' ? '600' : '500',
          fontSize: '14px',
          padding: '8px 16px',
          borderRadius: '6px',
          backgroundColor: location.pathname === '/area-log' ? '#eff6ff' : 'transparent',
          transition: 'all 0.2s'
        }}
      >
        Area Log
      </Link>
      <Link 
        to="/physical-log" 
        style={{
          color: location.pathname === '/physical-log' ? '#3b82f6' : '#6b7280',
          textDecoration: 'none',
          fontWeight: location.pathname === '/physical-log' ? '600' : '500',
          fontSize: '14px',
          padding: '8px 16px',
          borderRadius: '6px',
          backgroundColor: location.pathname === '/physical-log' ? '#eff6ff' : 'transparent',
          transition: 'all 0.2s'
        }}
      >
        Physical Log Entry
      </Link>

      <Link to="/batch-tracker" style={{
          color: location.pathname === '/batch-tracker' ? '#3b82f6' : '#6b7280',
          textDecoration: 'none',
          fontWeight: location.pathname === '/batch-tracker' ? '600' : '500',
          fontSize: '14px',
          padding: '8px 16px',
          borderRadius: '6px',
          backgroundColor: location.pathname === '/batch-tracker' ? '#eff6ff' : 'transparent',
          transition: 'all 0.2s'
        }}>Batch Tracker</Link>

      {/* <Link to="/dispatch-tracker" style={{
          color: location.pathname === '/dispatch-tracker' ? '#3b82f6' : '#6b7280',
          textDecoration: 'none',
          fontWeight: location.pathname === '/dispatch-tracker' ? '600' : '500',
          fontSize: '14px',
          padding: '8px 16px',
          borderRadius: '6px',
          backgroundColor: location.pathname === '/dispatch-tracker' ? '#eff6ff' : 'transparent',
          transition: 'all 0.2s'
        }}>Dispatch Tracker</Link> */}

      {/* <Link to="/packing-dispatch-tracker" style={{
          color: location.pathname === '/packing-dispatch-tracker' ? '#3b82f6' : '#6b7280',
          textDecoration: 'none',
          fontWeight: location.pathname === '/packing-dispatch-tracker' ? '600' : '500',
          fontSize: '14px',
          padding: '8px 16px',
          borderRadius: '6px',
          backgroundColor: location.pathname === '/packing-dispatch-tracker' ? '#eff6ff' : 'transparent',
          transition: 'all 0.2s'
        }}>Dispatch Tracker</Link> */}

      <Link to="/manufacturing-dispatch-tracker" style={{
          color: location.pathname === '/manufacturing-dispatch-tracker' ? '#3b82f6' : '#6b7280',
          textDecoration: 'none',
          fontWeight: location.pathname === '/manufacturing-dispatch-tracker' ? '600' : '500',
          fontSize: '14px',
          padding: '8px 16px',
          borderRadius: '6px',
          backgroundColor: location.pathname === '/manufacturing-dispatch-tracker' ? '#eff6ff' : 'transparent',
          transition: 'all 0.2s'
        }}>Dispatch Tracker</Link>

      <Link to="/audit-trail-report" style={{
          color: location.pathname === '/audit-trail-report' ? '#3b82f6' : '#6b7280',
          textDecoration: 'none',
          fontWeight: location.pathname === '/audit-trail-report' ? '600' : '500',
          fontSize: '14px',
          padding: '8px 16px',
          borderRadius: '6px',
          backgroundColor: location.pathname === '/audit-trail-report' ? '#eff6ff' : 'transparent',
          transition: 'all 0.2s'
        }}>Audit trail</Link>

      {/* Area Planning Dropdown */}
      <div
        ref={areaDropdownRef}
        style={{ position: 'relative' }}
        onMouseEnter={() => setAreaDropdownOpen(true)}
        onMouseLeave={() => setAreaDropdownOpen(false)}
      >
        <button
          onClick={() => setAreaDropdownOpen(!areaDropdownOpen)}
          style={{
            color: isAreaActive ? '#3b82f6' : '#6b7280',
            textDecoration: 'none',
            fontWeight: isAreaActive ? '600' : '500',
            fontSize: '14px',
            padding: '8px 16px',
            borderRadius: '6px',
            backgroundColor: isAreaActive ? '#eff6ff' : 'transparent',
            transition: 'all 0.2s',
            border: 'none',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '4px'
          }}
        >
          Area Planning
          <span style={{
            fontSize: '10px',
            transform: areaDropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.2s'
          }}>▼</span>
        </button>

        {areaDropdownOpen && (
          <div style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            backgroundColor: 'white',
            borderRadius: '8px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            padding: '10px 0',
            minWidth: '250px',
            zIndex: 1000,
            border: '1px solid #e5e7eb'
          }}>
            {areaMenuItems.map((item) => (
              <div
                key={item.path}
                onClick={() => {
                  navigate(item.path);
                  setAreaDropdownOpen(false);
                }}
                style={{
                  padding: '12px 18px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  lineHeight: '1.5',
                  color: location.pathname === item.path ? '#3b82f6' : '#374151',
                  backgroundColor: location.pathname === item.path ? '#eff6ff' : 'transparent',
                  fontWeight: location.pathname === item.path ? '600' : '400',
                  transition: 'background-color 0.15s',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px'
                }}
                onMouseEnter={(e) => {
                  if (location.pathname !== item.path) {
                    e.currentTarget.style.backgroundColor = '#f3f4f6';
                  }
                }}
                onMouseLeave={(e) => {
                  if (location.pathname !== item.path) {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }
                }}
              >
                {item.label}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* R&D Analytics Dropdown */}
      <div
        ref={rndDropdownRef}
        style={{ position: 'relative' }}
        onMouseEnter={() => setRndDropdownOpen(true)}
        onMouseLeave={() => setRndDropdownOpen(false)}
      >
        <button
          onClick={() => setRndDropdownOpen(!rndDropdownOpen)}
          style={{
            color: isRndActive ? '#3b82f6' : '#6b7280',
            textDecoration: 'none',
            fontWeight: isRndActive ? '600' : '500',
            fontSize: '14px',
            padding: '8px 16px',
            borderRadius: '6px',
            backgroundColor: isRndActive ? '#eff6ff' : 'transparent',
            transition: 'all 0.2s',
            border: 'none',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '4px'
          }}
        >
          R&D Analytics
          <span style={{
            fontSize: '10px',
            transform: rndDropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.2s'
          }}>▼</span>
        </button>

        {rndDropdownOpen && (
          <div style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            backgroundColor: 'white',
            borderRadius: '8px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            padding: '10px 0',
            minWidth: '220px',
            zIndex: 1000,
            border: '1px solid #e5e7eb'
          }}>
            {rndMenuItems.map((item) => (
              <div
                key={item.path}
                onClick={() => {
                  navigate(item.path);
                  setRndDropdownOpen(false);
                }}
                style={{
                  padding: '12px 18px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  lineHeight: '1.5',
                  color: location.pathname === item.path ? '#3b82f6' : '#374151',
                  backgroundColor: location.pathname === item.path ? '#eff6ff' : 'transparent',
                  fontWeight: location.pathname === item.path ? '600' : '400',
                  transition: 'background-color 0.15s',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px'
                }}
                onMouseEnter={(e) => {
                  if (location.pathname !== item.path) {
                    e.currentTarget.style.backgroundColor = '#f3f4f6';
                  }
                }}
                onMouseLeave={(e) => {
                  if (location.pathname !== item.path) {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }
                }}
              >
                {item.label}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Physical Log Insights Dropdown */}
      <div 
        ref={physicalDropdownRef}
        style={{ position: 'relative' }}
        onMouseEnter={() => setPhysicalDropdownOpen(true)}
        onMouseLeave={() => setPhysicalDropdownOpen(false)}
      >
        <button
          onClick={() => setPhysicalDropdownOpen(!physicalDropdownOpen)}
          style={{
            color: isPhysicalActive ? '#3b82f6' : '#6b7280',
            textDecoration: 'none',
            fontWeight: isPhysicalActive ? '600' : '500',
            fontSize: '14px',
            padding: '8px 16px',
            borderRadius: '6px',
            backgroundColor: isPhysicalActive ? '#eff6ff' : 'transparent',
            transition: 'all 0.2s',
            border: 'none',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '4px'
          }}
        >
          Physical Log Insights
          <span style={{ 
            fontSize: '10px', 
            transform: physicalDropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.2s'
          }}>▼</span>
        </button>
        
        {physicalDropdownOpen && (
          <div style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            backgroundColor: 'white',
            borderRadius: '8px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            padding: '10px 0',
            minWidth: '240px',
            zIndex: 1000,
            border: '1px solid #e5e7eb'
          }}>
            {physicalMenuItems.map((item) => (
              <div
                key={item.path}
                onClick={() => {
                  navigate(item.path);
                  setPhysicalDropdownOpen(false);
                }}
                style={{
                  padding: '12px 18px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  lineHeight: '1.5',
                  color: location.pathname === item.path ? '#3b82f6' : '#374151',
                  backgroundColor: location.pathname === item.path ? '#eff6ff' : 'transparent',
                  fontWeight: location.pathname === item.path ? '600' : '400',
                  transition: 'background-color 0.15s',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px'
                }}
                onMouseEnter={(e) => {
                  if (location.pathname !== item.path) {
                    e.currentTarget.style.backgroundColor = '#f3f4f6';
                  }
                }}
                onMouseLeave={(e) => {
                  if (location.pathname !== item.path) {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }
                }}
              >
                {item.label}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Statistics Dropdown */}
      <div 
        ref={dropdownRef}
        style={{ position: 'relative' }}
        onMouseEnter={() => setStatsDropdownOpen(true)}
        onMouseLeave={() => setStatsDropdownOpen(false)}
      >
        <button
          onClick={() => setStatsDropdownOpen(!statsDropdownOpen)}
          style={{
            color: isStatsActive ? '#3b82f6' : '#6b7280',
            textDecoration: 'none',
            fontWeight: isStatsActive ? '600' : '500',
            fontSize: '14px',
            padding: '8px 16px',
            borderRadius: '6px',
            backgroundColor: isStatsActive ? '#eff6ff' : 'transparent',
            transition: 'all 0.2s',
            border: 'none',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '4px'
          }}
        >
          Equipment Log Insights
          <span style={{ 
            fontSize: '10px', 
            transform: statsDropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)',
            transition: 'transform 0.2s'
          }}>▼</span>
        </button>
        
        {statsDropdownOpen && (
          <div style={{
            position: 'absolute',
            top: '100%',
            left: 0,
            backgroundColor: 'white',
            borderRadius: '8px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
            padding: '10px 0',
            minWidth: '200px',
            zIndex: 1000,
            border: '1px solid #e5e7eb'
          }}>
            {statsMenuItems.map((item) => (
              <div
                key={item.path}
                onClick={() => {
                  navigate(item.path);
                  setStatsDropdownOpen(false);
                }}
                style={{
                  padding: '12px 18px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  lineHeight: '1.5',
                  color: location.pathname === item.path ? '#3b82f6' : '#374151',
                  backgroundColor: location.pathname === item.path ? '#eff6ff' : 'transparent',
                  fontWeight: location.pathname === item.path ? '600' : '400',
                  transition: 'background-color 0.15s',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '10px'
                }}
                onMouseEnter={(e) => {
                  if (location.pathname !== item.path) {
                    e.currentTarget.style.backgroundColor = '#f3f4f6';
                  }
                }}
                onMouseLeave={(e) => {
                  if (location.pathname !== item.path) {
                    e.currentTarget.style.backgroundColor = 'transparent';
                  }
                }}
              >
                {item.label}
              </div>
            ))}
          </div>
        )}
      </div>

    </nav>
  );
};

function App() {
  return (
    <Router>
      <div style={{ fontFamily: 'system-ui, -apple-system, sans-serif' }}>
        <Navigation />
        <Routes>
          <Route path="/" element={<EquipmentPlanner />} />
          <Route path="/tracker" element={<ConsolidatedTracker />} />
          <Route path="/plan-create" element={<EquipmentPlanCreate />} />
          <Route path="/area-plan-create" element={<AreaPlanCreate />} />
          <Route path="/area-tracker" element={<AreaConsolidatedTracker />} />
          <Route path="/area-plan-vs-actual" element={<AreaPlanVsActual />} />
          <Route path="/area-log-new" element={<AreaLogNew />} />
          <Route path="/area-micro-monitoring" element={<AreaMicroMonitoring />} />
          <Route path="/area-log" element={<AreaLog />} />
          <Route path="/physical-log" element={<PhysicalLogEntry />} />
          <Route path="/volume-summary" element={<VolumeSummary />} />
          <Route path="/log-accuracy" element={<EquipmentLogAccuracyDashboard />} />
          <Route path="/production-variance" element={<ProductionVarianceDashboard />} />
          <Route path="/plan-cycle-variance" element={<PlanCycleVariance />} />
          <Route path="/cleaning-variance" element={<CleaningTimeVariance />} />
          <Route path="/audit-trail-report" element={<ATReport />} />
          <Route path="/physical-log-insights/tracker" element={<PhysicalConsolidatedTracker />} />
          <Route path="/physical-log-insights/volume-summary" element={<PhysicalVolumeSummary />} />
          <Route path="/physical-log-insights/production-variance" element={<PhysicalProductionVarianceDashboard />} />
          <Route path="/batch-tracker" element={<BatchTracker />} />
          <Route path="/dispatch-tracker" element={<OsdDispatch />} />
          <Route path="/packing-dispatch-tracker" element={<PackingDispatch />} />
          <Route path="/manufacturing-dispatch-tracker" element={<ManufacturingDispatch />} />
          <Route path="/rnd" element={<RndSampleTracking />} />
          <Route path="/rnd/plan-create" element={<RndEquipmentPlanCreate />} />
          <Route path="/rnd/physical-log" element={<RndPhysicalLogEntry />} />
        </Routes>
      </div>
    </Router>
  );
}

export default App;
